import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap } from 'rxjs';
import { MonitorSegment } from 'src/app/models/monitor-segments.model';
import { MonitorService } from 'src/app/services/monitor.service';
import { ToastService } from 'src/app/services/toast.service';

import MonitorActions from './actions';

@Injectable()
export class MonitorEffects {
  private readonly actions$ = inject(Actions);
  private readonly monitorService = inject(MonitorService);
  private readonly toastService = inject(ToastService);

  loadAllMonitorData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitorActions.loadAllMonitorData),
      switchMap(() =>
        Object.values(MonitorSegment).map((segment) =>
          MonitorActions.loadMonitorSegmentData({ segment }),
        ),
      ),
    ),
  );

  loadMonitorSegmentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitorActions.loadMonitorSegmentData),
      mergeMap(({ segment }) =>
        this.monitorService.getMonitorData(segment).pipe(
          map((data) =>
            MonitorActions.monitorSegmentDataLoaded({
              segment,
              data,
            }),
          ),
          catchError(() => [MonitorActions.loadMonitorSegmentDataFailure({})]),
        ),
      ),
    ),
  );

  loadMonitorSegmentDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MonitorActions.loadMonitorSegmentDataFailure),
        map(({ message }) => {
          this.toastService.showWarning(
            message ?? 'Er is iets mis gegaan bij het ophalen van de gegevens.',
          );
        }),
      ),
    { dispatch: false },
  );

  monitorSegmentDataLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonitorActions.monitorSegmentDataLoaded),
      switchMap(({ segment, data }) => {
        const setAdvice = MonitorActions.setMonitorSegmentAdvice({
          segment,
          advice: { adviceText: data.adviceText },
        });

        const setPeriods = MonitorActions.setMonitorSegmentPeriods({
          segment,
          periods: {
            currentPeriod: data.currentPeriod,
            previousPeriod: data.previousPeriod,
          },
        });

        const setMonitorScoreAndTrend =
          MonitorActions.setMonitorSegmentScoreAndTrend({
            segment,
            score: data.mowingGrade,
            trend: data.trend,
          });

        return [setAdvice, setPeriods, setMonitorScoreAndTrend];
      }),
    ),
  );
}
